import { memo, useState } from 'react';

import { Button, Flex, Heading, Separator, Text } from '@/components/core';
import { TEMPLATES } from '@/components/ui';
import { Step, StepProps, TemplateCard } from '@/features/agents';
import { keys } from '@/utils';

type TemplateValue = keyof typeof TEMPLATES | undefined;
export type ChooseTemplateResult = { template: TemplateValue };

export type ChooseTemplateStepProps = StepProps & {
  onContinue: (result: ChooseTemplateResult) => void;
  defaultValue?: TemplateValue;
};

const IChooseTemplateStep = ({ onContinue, defaultValue, ...rest }: ChooseTemplateStepProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateValue>(defaultValue);

  return (
    <Step {...rest}>
      <Flex col className="gap-md">
        <Flex col className="gap-lg px-md">
          <Flex col className="gap-xs">
            <Heading size="md">Let's setup your AI agent</Heading>
            <Text>
              Choose the document type that’s <strong>most similar</strong> to your use case. <br />
              We’ll use it to set up your AI model — you can easily customize it later.
            </Text>
          </Flex>

          <Flex>
            <div className="grid grid-cols-4 gap-3">
              {keys(TEMPLATES).map((template) => (
                <TemplateCard
                  isSelected={template === selectedTemplate}
                  key={template}
                  template={template}
                  onPress={() => setSelectedTemplate(template)}
                />
              ))}
            </div>
          </Flex>
        </Flex>

        <Separator orientation="horizontal" />

        <Flex col className="px-md">
          <Button
            variant="primary"
            isDisabled={!selectedTemplate}
            onPress={() => onContinue({ template: selectedTemplate })}
          >
            <Text>Continue</Text>
          </Button>
        </Flex>
      </Flex>
    </Step>
  );
};

export const ChooseTemplateStep = memo(IChooseTemplateStep) as typeof IChooseTemplateStep;
